import {
  Datagrid,
  useListContext,
  useDataProvider,
  useNotify,
  Button,
  Confirm,
  FilterButton,
  ExportButton,
} from 'react-admin'
import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react'
import { SelectAll as SelectAllIcon } from '@mui/icons-material'
import { Box } from '@mui/material'

import { useColumnPreferences } from '../hooks/useColumnPreferences'
import { ColumnSettingsButton } from './ColumnSettingsButton'
import { useToolbarContext } from '../toolbar/ToolbarContext'

// Constants for positioning and timing
const MAX_SELECTABLE_RECORDS = 10000 // Upper limit to prevent performance issues

const POSITIONING_DELAYS = {
  QUICK: 50,
  MEDIUM: 100,
  FINAL_VISIBILITY: 250, // Delay before showing button to ensure layout is stable
}

const LAYOUT_CONSTANTS = {
  TEXT_GAP: 16, // Gap in pixels between selection text and button
  FALLBACK_LEFT_OFFSET: 220, // Fallback position if selection text element not found
}

// React Admin CSS class names (internal API - subject to change)
// Tested with react-admin v4.x
const RA_SELECTORS = {
  TOOLBAR: '.RaBulkActionsToolbar-toolbar',
  TITLE: '.RaBulkActionsToolbar-title',
  LIST_CONTENT: '.RaList-content', // Container to observe for DOM changes
}

/**
 * Overlay component that appears on top of the bulk actions toolbar
 */
function SelectAllOverlay({ onSelectAll, total, isFetching }) {
  const overlayRef = useRef(null)
  const [isPositioned, setIsPositioned] = useState(false)
  const isPositioningRef = useRef(true) // Start as true (positioning)
  const isMountedRef = useRef(true) // Track if component is mounted to prevent memory leaks

  // Use layout effect to hide immediately before paint
  useLayoutEffect(() => {
    setIsPositioned(false)
    isPositioningRef.current = true
  }, [total, isFetching])

  useEffect(() => {
    // Reset mounted flag when effect runs
    isMountedRef.current = true

    // Find the bulk actions toolbar and position our overlay on top of it
    const findAndPositionOverlay = () => {
      // Early return if component unmounted
      if (!isMountedRef.current) {
        return
      }

      try {
        const toolbar = document.querySelector(RA_SELECTORS.TOOLBAR)
        if (!toolbar || !overlayRef.current) {
          return
        }

        const rect = toolbar.getBoundingClientRect()

        // Verify toolbar is actually visible and has dimensions
        if (!rect.width || !rect.height) {
          return
        }

        // Match the toolbar's exact position and height
        overlayRef.current.style.top = `${rect.top}px`
        overlayRef.current.style.height = `${rect.height}px`

        // Find the "X items selected" text element and position after it
        const selectionText = toolbar.querySelector(RA_SELECTORS.TITLE)
        if (selectionText) {
          const textRect = selectionText.getBoundingClientRect()
          // Position right after the text with a small gap
          overlayRef.current.style.left = `${textRect.right + LAYOUT_CONSTANTS.TEXT_GAP}px`
        } else {
          // Fallback if we can't find the text element
          overlayRef.current.style.left = `${rect.left + LAYOUT_CONSTANTS.FALLBACK_LEFT_OFFSET}px`
        }
      } catch (error) {
        // Silently fail - positioning errors are non-critical
      }
    }

    // Initial positioning with multiple attempts to ensure toolbar is rendered
    findAndPositionOverlay()
    const timeoutId1 = setTimeout(() => findAndPositionOverlay(), POSITIONING_DELAYS.QUICK)
    const timeoutId2 = setTimeout(() => findAndPositionOverlay(), POSITIONING_DELAYS.MEDIUM)

    // Only mark as positioned and show after final positioning
    const timeoutId3 = setTimeout(() => {
      if (!isMountedRef.current) return
      findAndPositionOverlay()
      isPositioningRef.current = false
      setIsPositioned(true)
    }, POSITIONING_DELAYS.FINAL_VISIBILITY)

    // Watch for changes in the DOM (toolbar appearing/disappearing)
    // Scope observer to list content container instead of entire body for better performance
    const observer = new MutationObserver(() => {
      if (!isPositioningRef.current && isMountedRef.current) {
        findAndPositionOverlay()
      }
    })

    const listContent = document.querySelector(RA_SELECTORS.LIST_CONTENT)
    if (listContent) {
      observer.observe(listContent, {
        childList: true,
        subtree: false, // Don't observe deep children for performance
        attributes: true,
        attributeFilter: ['class', 'style'],
      })
    }

    // Reposition on scroll or resize (only if visible)
    const handleReposition = () => {
      if (!isPositioningRef.current && isMountedRef.current) {
        findAndPositionOverlay()
      }
    }

    // Scope scroll listener to the list content container instead of window for better performance
    const scrollContainer = document.querySelector('.RaLayout-content')
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleReposition)
    }
    window.addEventListener('resize', handleReposition)

    return () => {
      // Mark as unmounted immediately to stop all pending operations
      isMountedRef.current = false
      clearTimeout(timeoutId1)
      clearTimeout(timeoutId2)
      clearTimeout(timeoutId3)
      observer.disconnect()
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleReposition)
      }
      window.removeEventListener('resize', handleReposition)
    }
  }, [total, isFetching])

  return (
    <Box
      ref={overlayRef}
      className="select-all-overlay"
      sx={{
        position: 'fixed',
        display: 'flex',
        alignItems: 'center',
        pointerEvents: 'none',
        zIndex: 1300,
        visibility: isPositioned ? 'visible' : 'hidden',
      }}
    >
      {isPositioned && (
        <Box sx={{ pointerEvents: 'auto' }}>
          {isFetching ? (
            <Button label={`Fetching ${total} records...`} disabled size="small">
              <SelectAllIcon />
            </Button>
          ) : (
            <Button label={`Select All ${total}`} onClick={onSelectAll} size="small">
              <SelectAllIcon />
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

/**
 * Custom Datagrid that allows selecting all filtered records (across all pages)
 * via a prompt that appears in the bulk actions toolbar.
 *
 * Also supports user-customizable column visibility and ordering when
 * enableColumnSettings is true (default).
 */
export function DatagridWithSelectAll({ children, bulkActionButtons, enableColumnSettings = true, ...props }) {
  const { data, total, selectedIds, onSelect, filterValues, resource, sort } = useListContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const isFetchingRef = useRef(false)
  const isMountedRef = useRef(true)
  const [isFetchingAll, setIsFetchingAll] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Column preferences hook
  const {
    children: processedChildren,
    allColumns,
    loading: columnsLoading,
    saving: columnsSaving,
    hasLocalChanges,
    toggleColumnVisibility,
    reorderColumns,
    savePreferences,
    resetToDefaults,
    discardChanges,
  } = useColumnPreferences(children)

  // Check if we should show "select all" prompt
  const shouldShowSelectAllPrompt =
    !isFetchingAll &&
    selectedIds?.length === data?.length &&
    total > (data?.length ?? 0) &&
    (selectedIds?.length ?? 0) > 0

  // Determine how many records to fetch (capped for performance)
  const recordsToSelect = Math.min(total, MAX_SELECTABLE_RECORDS)
  const exceedsCap = total > MAX_SELECTABLE_RECORDS

  // Function to fetch and select all filtered records
  const fetchAllRecords = useCallback(() => {
    if (isFetchingRef.current) {
      return // Already fetching, prevent duplicate requests
    }

    // Prevent race conditions by immediately marking as fetching
    isFetchingRef.current = true

    // Fetch and select all filtered records
    setIsFetchingAll(true)

    dataProvider
      .getList(resource, {
        pagination: { page: 1, perPage: recordsToSelect },
        sort,
        filter: filterValues,
      })
      .then(({ data: allData }) => {
        // Check if component unmounted
        if (!isMountedRef.current) {
          return
        }

        const allIds = allData.map((record) => record.id)
        onSelect(allIds)
        setIsFetchingAll(false)
        isFetchingRef.current = false
        notify(`Successfully selected ${allIds.length} records`, { type: 'success', autoHideDuration: 3000 })
      })
      .catch((error) => {
        // Check if component unmounted
        if (!isMountedRef.current) {
          return
        }

        setIsFetchingAll(false)
        isFetchingRef.current = false
        notify('Failed to fetch all records. Please try again.', { type: 'error' })
        // Reset selection to visible items only
        onSelect(data?.map((record) => record.id) ?? [])
      })
  }, [recordsToSelect, resource, sort, filterValues, dataProvider, onSelect, data, notify])

  // Function to handle select all click
  const handleSelectAllRecords = useCallback(() => {
    if (exceedsCap) {
      setConfirmOpen(true)
    } else {
      fetchAllRecords()
    }
  }, [exceedsCap, fetchAllRecords])

  // Handle confirm dialog
  const handleConfirm = useCallback(() => {
    setConfirmOpen(false)
    fetchAllRecords()
  }, [fetchAllRecords])

  const handleDialogClose = useCallback(() => {
    setConfirmOpen(false)
    isFetchingRef.current = false
  }, [])

  // Cleanup function on unmount
  useEffect(
    () => () => {
      isMountedRef.current = false // Mark component as unmounted
      isFetchingRef.current = false
    },
    []
  )

  // Use processed children if column settings enabled, otherwise original
  const displayChildren = enableColumnSettings ? processedChildren : children

  // Get toolbar props from context (filter, export, children buttons)
  const toolbarCtx = useToolbarContext()
  const toolbarProps = toolbarCtx?.toolbarPropsRef?.current

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
        {toolbarProps?.leadingChildren}
        <FilterButton filters={toolbarCtx?.filters} />
        {toolbarProps?.children}
        {toolbarProps?.customExportButton ??
          (toolbarProps && (
            <ExportButton
              exporter={toolbarProps.customExporter}
              maxResults={toolbarProps.maxResults}
              disabled={toolbarProps.disabled}
              label={toolbarProps.label}
              onClick={toolbarProps.onClick}
            />
          ))}
        {enableColumnSettings && allColumns.length > 0 && (
          <ColumnSettingsButton
            allColumns={allColumns}
            onToggleVisibility={toggleColumnVisibility}
            onReorder={reorderColumns}
            onSave={savePreferences}
            onReset={resetToDefaults}
            onDiscard={discardChanges}
            hasChanges={hasLocalChanges}
            saving={columnsSaving}
            loading={columnsLoading}
          />
        )}
      </Box>
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {(shouldShowSelectAllPrompt || isFetchingAll) && (
          <SelectAllOverlay onSelectAll={handleSelectAllRecords} total={total} isFetching={isFetchingAll} />
        )}
        <Datagrid {...props} bulkActionButtons={bulkActionButtons}>
          {displayChildren}
        </Datagrid>
      </Box>
      <Confirm
        isOpen={confirmOpen}
        loading={isFetchingAll}
        title="Select All Records"
        content={`Total filtered records: ${total}\n\nThis exceeds the maximum of ${MAX_SELECTABLE_RECORDS} records.\n\nSelect the first ${MAX_SELECTABLE_RECORDS} records instead?\n\n(Click Cancel to abort, or use filters to narrow down your selection)`}
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </>
  )
}
